import { Component, inject, OnInit } from '@angular/core';
import { NgFor } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import {
    FormsModule,
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatListModule } from '@angular/material/list';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { TranslocoModule } from '@ngneat/transloco';
import { ApiService } from '../../services/api/api.service';
import { Company } from '../../interfaces/Company';
import { CompanyResponse } from '../../interfaces/CompanyResponse';
import { BranchOffice } from './../../interfaces/BranchOffice';
import { BranchOfficeResponse } from './../../interfaces/BranchOfficeResponse';
import { CompanyUser } from '../../interfaces/CompanyUser';
import { Severity } from '../../interfaces/Severity';
import { SeverityResponse } from '../../interfaces/SeverityResponse';
import { CompanyUserResponse } from '../../interfaces/CompanyUserResponse';
import { VulnerabilityOrigin } from '../../interfaces/VulnerabilityOrigin';
import { VulnerabilityOriginResponse } from '../../interfaces/VulnerabilityOriginResponse';
import { VulnerabilityAssessment } from '../../interfaces/VulnerabilityAssessment';
import { VulnerabilityAssessmentResponse } from '../../interfaces/VulnerabilityAssessmentResponse';
import { AlertDialogComponent } from '../../common/alert-dialog/alert-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { GlobalConstants } from '../../common/global-constants';

@Component({
    selector: 'app-v-edit-assessment',
    standalone: true,
    imports: [
        MatTableModule,
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        RouterLink,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatListModule,
        ReactiveFormsModule,
        TranslocoModule,
        NgFor,
    ],
    templateUrl: './v-edit-assessment.component.html',
    styleUrl: './v-edit-assessment.component.scss',
})
export class VEditAssessmentComponent implements OnInit {
    // Id
    id: string;

    // Vulnerability Assessment
    editAssessment: VulnerabilityAssessment;

    // isToggled
    isToggled = false;

    // editAssessmentForm
    editAssessmentForm: FormGroup;

    // Array of severities
    severities: Severity[] = [];

    selectedSeverity: Severity | undefined;

    // Array of vulnerability origin
    vulnerabilityOrigins: VulnerabilityOrigin[] = [];

    selectedVulnerabilityOrigin: VulnerabilityOrigin | undefined;

    // Array of companies
    companies: Company[] = [];

    selectedCompany: string = '';

    // Array of branch offices
    branchOffices: BranchOffice[] = [];

    selectedBranchOffice: BranchOffice | undefined;

    // Array of company users
    companyUsersList: CompanyUser[] = [];

    selectedUsers: string[] = [];

    // Show alert
    showAlert: boolean = false;

    readonly dialog = inject(MatDialog);

    constructor(
        private fb: FormBuilder,
        public themeService: CustomizerSettingsService,
        private apiService: ApiService,
        private router: Router,
        private route: ActivatedRoute
    ) {
        this.editAssessmentForm = this.fb.group({
            assessmentName: [
                '',
                [Validators.required, Validators.maxLength(100)],
            ],
            description: ['', [Validators.maxLength(300)]],
            startDate: [{ value: '', disabled: true }, [Validators.required]],
            endDate: [{ value: '', disabled: true }, []],
            branchOffice: [this.selectedBranchOffice, [Validators.required]],
            severity: [this.selectedSeverity, [Validators.required]],
            vulnerabilityOrigin: [
                this.selectedVulnerabilityOrigin,
                [Validators.required],
            ],
            companyUsers: [this.selectedUsers, [Validators.required]],
        });
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for role name
     */
    get roleName(): string {
        return localStorage.getItem('roleName') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    ngOnInit() {
        this.route.params.subscribe((params) => {
            this.id = params['id'];

            this.apiService
                .getVulnerabilityAssessmentById(
                    this.id,
                    this.roleName == GlobalConstants.internal
                )
                .subscribe((data: VulnerabilityAssessmentResponse) => {
                    this.editAssessment = data.vulnerabilityAssessment;
                    if (this.editAssessment != undefined) {
                        this.apiService
                            .getCompanyPagination(0, 1000000, 'name', 'asc')
                            .subscribe((response: CompanyResponse) => {
                                this.companies.push(...response.companies);
                                this.selectedCompany =
                                    this.editAssessment.branchOffice.company.id;
                                this.apiService
                                    .getBranchOfficeByCompanyId(
                                        this.selectedCompany,
                                        0,
                                        1000000,
                                        'name',
                                        'asc'
                                    )
                                    .subscribe(
                                        (response: BranchOfficeResponse) => {
                                            if (
                                                response.branchOffices.length >
                                                0
                                            ) {
                                                this.branchOffices.push(
                                                    ...response.branchOffices
                                                );
                                                this.selectedBranchOffice =
                                                    this.branchOffices.find(
                                                        ({ id }) =>
                                                            id ===
                                                            this.editAssessment
                                                                .branchOffice.id
                                                    );
                                                this.apiService
                                                    .getCompanyUserByBranchOfficeId(
                                                        this.editAssessment
                                                            .branchOffice.id,
                                                        0,
                                                        1000000,
                                                        'name',
                                                        'asc'
                                                    )
                                                    .subscribe(
                                                        (
                                                            response: CompanyUserResponse
                                                        ) => {
                                                            if (
                                                                response
                                                                    .companyUsers
                                                                    .length > 0
                                                            ) {
                                                                this.companyUsersList.push(
                                                                    ...response.companyUsers
                                                                );
                                                            }
                                                        }
                                                    );
                                            }
                                        }
                                    );
                            });
                        this.apiService
                            .getSeverity()
                            .subscribe((response: SeverityResponse) => {
                                this.severities.push(...response.severities);
                                this.selectedSeverity = this.severities.find(
                                    ({ id }) =>
                                        id === this.editAssessment.severity.id
                                );
                            });
                        this.apiService
                            .getVulnerabilityOrigin()
                            .subscribe(
                                (response: VulnerabilityOriginResponse) => {
                                    this.vulnerabilityOrigins.push(
                                        ...response.vulnerabilityOrigins
                                    );
                                    this.selectedVulnerabilityOrigin =
                                        this.vulnerabilityOrigins.find(
                                            ({ id }) =>
                                                id ===
                                                this.editAssessment
                                                    .vulnerabilityOrigin.id
                                        );
                                }
                            );
                    }
                });
        });
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    // Popup Trigger
    classApplied = false;
    toggleClass() {
        this.classApplied = !this.classApplied;
    }

    // Change company
    changeCompany(companyId: string) {
        this.branchOffices = [];
        this.companyUsersList = [];
        this.apiService
            .getBranchOfficeByCompanyId(companyId, 0, 1000000, 'name', 'asc')
            .subscribe((response: BranchOfficeResponse) => {
                if (response.branchOffices.length > 0) {
                    this.branchOffices.push(...response.branchOffices);
                }
            });
    }

    // Change branch office
    changeBranchOffice() {
        this.companyUsersList = [];
        if (this.selectedBranchOffice) {
            this.apiService
                .getCompanyUserByBranchOfficeId(
                    this.selectedBranchOffice.id,
                    0,
                    1000000,
                    'name',
                    'asc'
                )
                .subscribe((response: CompanyUserResponse) => {
                    if (response.companyUsers.length > 0) {
                        this.companyUsersList.push(...response.companyUsers);

                        this.editAssessment.companyUsers.forEach((user) => {
                            const result = response.companyUsers.find(
                                ({ id }) => id === user
                            );
                            if (result) {
                                this.selectedUsers.push(user);
                            }
                        });
                    }
                });
        }
    }

    /**
     * On submit
     */
    onSubmit(): void {
        // Return if the form is invalid
        if (this.editAssessmentForm.invalid || this.classApplied) {
            this.showAlert = false;
            return;
        }

        // Disable the form
        this.editAssessmentForm.disable();

        // Hide the alert
        this.showAlert = false;

        if (this.selectedBranchOffice) {
            this.editAssessment.branchOffice =
                this.editAssessmentForm.get('branchOffice')?.value;
        }
        // Edit Assesment
        this.apiService
            .patchVulnerabilityAssessment(this.id, this.editAssessment)
            .subscribe({
                next: (value) => {
                    // Re-enable the form
                    this.editAssessmentForm.enable();

                    // Show the alert
                    this.showAlert = true;
                },
                error: (error) => {
                    let errMsg: string = error;
                    const dialogRef = this.dialog.open(AlertDialogComponent, {
                        data: {
                            title: 'ERROR',
                            message: errMsg,
                        },
                    });
                },
                complete: () => {
                    this.router.navigateByUrl('/vulnerability/assessments');
                },
            });
    }
}
